import { centroid, featureCollection } from '@turf/turf';
import { Expression } from 'mapbox-gl';
import { Layer, Source } from 'react-map-gl';

import {
  asFieldsFeatureCollectionFromFeatures,
  hasPlotGeometry,
  PlotFeatureWithGeometry,
} from '@fields/data-access';
import { FieldsFeatureCollection } from '@fields/data-access';

export const PLOTS_LAYER_ID = 'plots-layer';
// import { PlotFeatureCollection } from '@data-access';

/* eslint-disable-next-line */
export interface PlotsLayerProps {
  data: FieldsFeatureCollection;
  id?: string;
  minZoom?: number;
  outline?: boolean;
  colorPolygon?: string;
  activePlotIds?: number[];
  beforeId?: string;
}

const polygonOutlineLayerStyle = {
  type: 'line' as const,
  paint: {
    'line-color': '#000',
    'line-width': 3,
  },
};

export function PlotsLayer(props: PlotsLayerProps) {
  const { colorPolygon } = props;
  const polygonLayerStyle = {
    type: 'fill' as const,
    paint: {
      'fill-color': colorPolygon || (['get', 'color'] as Expression),
      'fill-opacity': 0.5,
    },
  };

  const { id = PLOTS_LAYER_ID, minZoom = 0, outline = false } = props;

  const plotFeatures = props.data.features.filter(hasPlotGeometry);

  if (plotFeatures.length === 0) {
    return null;
  }

  const plotData = asFieldsFeatureCollectionFromFeatures(plotFeatures);

  const plotCenterFeatureCollection = featureCollection(
    plotFeatures.map((feature: PlotFeatureWithGeometry) => {
      const center = centroid(feature);
      return {
        type: 'Feature' as const,
        geometry: center.geometry,
        properties: {
          name: feature.properties.name,
        },
      };
    }),
  );

  return (
    <>
      <Source data={plotData} id="plots-data" type="geojson">
        {!outline ? (
          <Layer
            beforeId={props.beforeId}
            id={id}
            minzoom={minZoom}
            {...polygonLayerStyle}
          />
        ) : null}
        <Layer
          beforeId={props.beforeId}
          id={`${id}_outline`}
          minzoom={minZoom}
          {...polygonOutlineLayerStyle}
        />
      </Source>
      <Source
        data={plotCenterFeatureCollection}
        id="plots-center-data"
        type="geojson"
      >
        {!outline ? (
          <Layer
            beforeId={props.beforeId}
            id={`${id}_symbol`}
            layout={{
              'text-field': [
                'format',
                ['string', ['get', 'name']],
                { 'font-scale': 0.7 },
              ],
              'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
            }}
            minzoom={minZoom}
            paint={{
              'text-color': '#fff',
              'text-halo-color': '#000',
              'text-halo-width': 1.2,
            }}
            type="symbol"
          />
        ) : null}
      </Source>
    </>
  );
}

export default PlotsLayer;
